import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Input, ScrollableContainer, Title } from "@monorepo/components";
import { Loader } from "@monorepo/components";
import { getLocalItem, LocaleStorageKeysEnum, setLocalItem } from "@monorepo/helpers";
import { removeLocalItem } from "@monorepo/helpers";
import { useGetClientCouponsQuery, useGetReservedClientCouponsQuery } from "@monorepo/services/api/coupons";
import { useAddGlobalCouponMutation, useRemoveGlobalCouponMutation } from "@monorepo/services/api/order";
import theme from "@monorepo/styles/theme";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Typography } from "@mui/material";

import { CouponCard } from "./Coupon/CouponCard";

interface IFormData {
  code: string;
}

interface IProps {
  onGlobalCouponUsed: () => void;
  onClientCouponUsed: () => void;
  onCouponRemoved: () => void;
  shouldResetError: boolean;
}

const PaymentCoupons: React.FC<IProps> = ({
  onGlobalCouponUsed,
  onClientCouponUsed,
  onCouponRemoved,
  shouldResetError,
}: IProps) => {
  const queries = [useGetReservedClientCouponsQuery(), useGetClientCouponsQuery()];
  const { t } = useTranslation();
  const coupons = queries.flatMap(({ data }) => data || []);
  const { handleSubmit, register, reset } = useForm<IFormData>({
    mode: "all",
    reValidateMode: "onChange",
  });

  const [addGlobalCoupon, { isLoading: isAdding }] = useAddGlobalCouponMutation();
  const [removeGlobalCoupon, { isLoading: isRemoving }] = useRemoveGlobalCouponMutation();

  const [globalError, setGlobalError] = useState<string>();
  const [storedGlobalCode, setStoredGlobalCode] = useState<string | null>(
    getLocalItem(LocaleStorageKeysEnum.GLOBAL_COUPON)
  );

  useEffect(() => {
    if (shouldResetError) {
      setGlobalError("");
      reset();
    }
  }, [shouldResetError, reset]);

  const handleApplyCoupon = useCallback(
    (values: IFormData) => {
      addGlobalCoupon(values.code)
        .unwrap()
        .then(() => {
          onGlobalCouponUsed();
          toast.success(t("coupons.added_successfully"));
          setLocalItem(LocaleStorageKeysEnum.GLOBAL_COUPON, values.code);
          setStoredGlobalCode(values.code);
        })
        .catch((error) => {
          setGlobalError(error.message);
        });
    },
    [t, addGlobalCoupon, onGlobalCouponUsed]
  );

  const handleResetCoupon = useCallback(async () => {
    if (!storedGlobalCode) return;

    removeGlobalCoupon()
      .unwrap()
      .then(() => {
        toast.success(t("coupons.removed_successfully"));
        removeLocalItem(LocaleStorageKeysEnum.GLOBAL_COUPON);
        setStoredGlobalCode(null);
        reset();
        onCouponRemoved();
      })
      .catch((error) => {
        setGlobalError(error.message);
      });
  }, [storedGlobalCode, removeGlobalCoupon, reset, t, onCouponRemoved]);

  const isQueryLoading = queries.some(({ isFetching }) => isFetching);

  return (
    <Box sx={{ rowGap: theme.spacing(3), justifyContent: "center", display: "grid" }}>
      <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "flex-start" }}>
        <Title sx={{ marginBottom: 0, marginLeft: 0 }} noMaxWidth={true}>
          {t("payments.coupon")}
        </Title>
      </Box>
      <form onSubmit={handleSubmit(handleApplyCoupon)}>
        <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 2 }}>
          <Input
            {...register("code")}
            label={t("payments.coupon")}
            sx={{ background: `${theme.palette.background.paper}!important` }}
            placeholder={t("payments.write_coupon")}
            disabled={!!storedGlobalCode}
            defaultValue={storedGlobalCode ?? ""}
          />
          {storedGlobalCode ? (
            <LoadingButton
              loading={isRemoving}
              onClick={handleResetCoupon}
              disableElevation
              variant="contained"
              color="secondary"
              sx={{ alignSelf: "end", height: "65%", backgroundColor: storedGlobalCode ? theme.palette.grey[300] : "" }}
            >
              {t("coupons.dont_use_coupon")}
            </LoadingButton>
          ) : (
            <LoadingButton
              loading={isAdding}
              type="submit"
              disableElevation
              variant="contained"
              color="primary"
              sx={{ alignSelf: "end", height: "65%" }}
            >
              {t("coupons.use_coupon")}
            </LoadingButton>
          )}
        </Box>
        {globalError && <Typography color="error">{globalError}</Typography>}
      </form>
      {isQueryLoading ? (
        <Loader />
      ) : (
        coupons.length > 0 && (
          <ScrollableContainer>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: theme.palette.text.secondary, marginBottom: 1 }}>
              {t("coupons.available_discounts")}
            </Typography>
            {coupons?.map((coupon) => (
              <CouponCard
                key={coupon.id}
                coupon={coupon}
                onCouponUsed={onClientCouponUsed}
                onCouponRemove={onCouponRemoved}
                shouldResetError={shouldResetError}
              />
            ))}
          </ScrollableContainer>
        )
      )}
    </Box>
  );
};

export default PaymentCoupons;
